import React from 'react';
import { Calendar, Sparkles, Users, Award, CheckCircle2, Star } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

export const EventManagement = () => {
  const eventImages = [
    {
      url: "https://customer-assets.emergentagent.com/job_insmart-media/artifacts/7xk5m2r4_3d7552cd-b13d-42b8-a366-928e64beb008.jpg",
      title: "Store Launch with Traditional Entertainment"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_insmart-media/artifacts/0xjtvor6_5ca9cf9a-51b8-4d65-ac04-f81faec41ba3.jpg",
      title: "Premium Floral Entrance Decoration"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_insmart-media/artifacts/95m2mz91_838a459b-fb34-4603-89ee-b2e71866e34d.jpg",
      title: "Professional Event Setup - Peter England"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_insmart-media/artifacts/nbx2rxpw_3476a076-9893-4441-baf5-c7809a8bca16.jpg",
      title: "Cultural Performance Integration"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_insmart-media/artifacts/gcs49rci_24424586-b460-451b-a43a-d0c85f90c34d.jpg",
      title: "Complete Event Management Services"
    }
  ];

  const eventServices = [
    {
      icon: Sparkles,
      title: "Store Inaugurations",
      description: "Grand openings with complete setup, decorations, and entertainment"
    },
    {
      icon: Calendar,
      title: "Corporate Events",
      description: "Product launches, brand activations, and business conferences"
    },
    {
      icon: Users,
      title: "Promotional Events",
      description: "Roadshows, exhibitions, and on-ground marketing campaigns"
    },
    {
      icon: Award,
      title: "Cultural Programs",
      description: "Traditional entertainment, dhol players, and cultural performances"
    }
  ];

  const eventFeatures = [
    "Complete event conceptualization and planning",
    "Professional venue decoration and branding",
    "Audio-visual equipment and lighting",
    "Traditional and modern entertainment arrangements",
    "Red carpet and VIP management",
    "Photography and videography services",
    "On-ground promotional activities",
    "Post-event marketing and social media coverage"
  ];

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="event-management" className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full mb-4">
            <Star size={20} />
            <span className="font-semibold">Complete Event Solutions</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Corporate Events & <span className="text-blue-600">Event Management in Solapur</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From concept to execution - We create memorable events that leave lasting impressions
          </p>
        </div>

        {/* Recent Event Showcase */}
        <div className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Award className="text-blue-600" />
              Recent Project: Peter England Store Launch
            </h3>
            <p className="text-gray-600 mb-6">
              Complete event management for premium retail store inauguration featuring traditional entertainment, 
              professional decorations, and seamless execution.
            </p>
            
            {/* Image Grid */}
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {eventImages.slice(0, 3).map((image, index) => (
                <div key={index} className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img 
                    src={image.url}
                    alt={image.title}
                    width="400"
                    height="256"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <p className="text-white text-sm font-semibold">{image.title}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {eventImages.slice(3).map((image, index) => (
                <div key={index} className="relative group overflow-hidden rounded-xl shadow-lg">
                  <img 
                    src={image.url}
                    alt={image.title}
                    width="600"
                    height="256"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <p className="text-white text-sm font-semibold">{image.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Event Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {eventServices.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card key={index} className="group hover:shadow-xl transition-all hover:scale-105 border-2 hover:border-blue-600">
                <CardContent className="p-6 text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                    <Icon className="text-blue-600 group-hover:text-white transition-colors" size={32} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Features Section */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 md:p-12 text-white mb-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-3xl font-bold mb-6">What We Offer in Event Management</h3>
              <p className="text-blue-100 mb-6 leading-relaxed">
                IN SMART MEDIA provides end-to-end event management services for retail launches, corporate events, 
                promotional activities, and brand activations. Our experienced team ensures every detail is perfect, 
                from planning to execution.
              </p>
              <Button 
                onClick={scrollToContact}
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50"
              >
                Plan Your Event
              </Button>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-4">Complete Event Services:</h4>
              <div className="grid grid-cols-1 gap-3">
                {eventFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle2 size={20} className="flex-shrink-0 mt-1" />
                    <span className="text-blue-100">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl font-bold text-blue-600 mb-2">50+</div>
            <div className="text-gray-700 font-medium">Events Managed</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
            <div className="text-gray-700 font-medium">Client Satisfaction</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl font-bold text-blue-600 mb-2">Premium</div>
            <div className="text-gray-700 font-medium">Brand Partners</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl font-bold text-blue-600 mb-2">Expert</div>
            <div className="text-gray-700 font-medium">Event Team</div>
          </div>
        </div>
      </div>
    </section>
  );
};
